/**
 * Fit to Panel — sizes an object to the brochure panel it sits in.
 *
 * Panels are the strips between consecutive fold lines (and the page edges). A panel's content area is inset by the fold line's
 * panel margin (its `gutter`) on a fold side and by the page margin guides on a page-edge side, so text stays clear of the
 * crease and the trim. Across the folds (the other axis) the content area runs between the page margins.
 */
import { isFoldGuide, DEFAULT_PANEL_GUTTER } from './foldPresets';

/** Page margins from the built-in margin guides (Page Design ▸ Margins); ¼ in on every side when there are none. */
export const marginBoxOf = (guides = [], page) => {
  const m = guides.filter((g) => g && g.kind === 'margin' && Number.isFinite(Number(g.position)));
  const v = m.filter((g) => g.orientation === 'vertical').map((g) => Number(g.position)).sort((a, b) => a - b);
  const h = m.filter((g) => g.orientation === 'horizontal').map((g) => Number(g.position)).sort((a, b) => a - b);
  const d = DEFAULT_PANEL_GUTTER;
  return {
    left: v.length ? v[0] : d,
    right: v.length > 1 ? page.width - v[v.length - 1] : v.length ? page.width - v[0] : d,
    top: h.length ? h[0] : d,
    bottom: h.length > 1 ? page.height - h[h.length - 1] : h.length ? page.height - h[0] : d,
    fromGuides: m.length > 0,
  };
};

/**
 * The panel layout of a page, or null when it has no fold lines.
 * `orientation` is the fold lines' orientation: 'vertical' folds make side-by-side panels (the axis is x), 'horizontal' folds
 * stacked panels (the axis is y). `panels[i]` = { start, end, innerStart, innerEnd } along that axis; `cross` = content
 * extent on the other axis (between the page margins).
 */
export const panelLayout = (guides = [], page) => {
  const folds = guides.filter(isFoldGuide);
  if (!folds.length || !page) return null;
  const vertical = folds.filter((f) => f.orientation === 'vertical').length;
  const orientation = vertical >= folds.length - vertical ? 'vertical' : 'horizontal';
  const along = orientation === 'vertical' ? page.width : page.height;
  const sorted = folds
    .filter((f) => f.orientation === orientation)
    .map((f) => ({ ...f, position: Math.min(along, Math.max(0, Number(f.position))) }))
    .sort((a, b) => a.position - b.position);
  const margins = marginBoxOf(guides, page);
  const edgeStart = orientation === 'vertical' ? margins.left : margins.top;
  const edgeEnd = orientation === 'vertical' ? margins.right : margins.bottom;
  const gutterOf = (f) => Math.max(0, Number(f.gutter) || 0);
  const bounds = [0, ...sorted.map((f) => f.position), along];
  const panels = [];
  for (let i = 0; i < bounds.length - 1; i += 1) {
    const start = bounds[i], end = bounds[i + 1];
    if (end - start < 1) continue; // fold on the page edge — no panel there
    const startFold = i > 0 ? sorted[i - 1] : null;
    const endFold = i < sorted.length ? sorted[i] : null;
    const innerStart = start + (startFold ? gutterOf(startFold) : edgeStart);
    const innerEnd = end - (endFold ? gutterOf(endFold) : edgeEnd);
    // `gutter`: the panel margin of the fold(s) bounding this panel — used as the column gutter by "panel columns"
    const gutter = Math.max(startFold ? gutterOf(startFold) : 0, endFold ? gutterOf(endFold) : 0);
    panels.push({ start, end, innerStart, innerEnd: Math.max(innerStart + 1, innerEnd), gutter });
  }
  const cross = orientation === 'vertical'
    ? { start: margins.top, end: Math.max(margins.top + 1, page.height - margins.bottom) }
    : { start: margins.left, end: Math.max(margins.left + 1, page.width - margins.right) };
  return { orientation, panels, cross, margins, foldCount: sorted.length };
};

/** Index of the panel that contains the object's centre along the fold axis (clamped to the first / last panel). */
export const panelIndexAt = (layout, object) => {
  if (!layout || !object) return -1;
  const centre = layout.orientation === 'vertical' ? object.x + object.width / 2 : object.y + object.height / 2;
  const i = layout.panels.findIndex((p) => centre >= p.start && centre < p.end);
  if (i >= 0) return i;
  return centre < layout.panels[0].start ? 0 : layout.panels.length - 1;
};

/**
 * New geometry for `object` fitted to its panel. `widthOnly` keeps the object's extent across the folds (its top/height for
 * side-by-side panels) and only fits the panel width; otherwise the object fills the panel's content area.
 */
export const fitToPanel = (layout, object, { widthOnly = false } = {}) => {
  const i = panelIndexAt(layout, object);
  if (i < 0) return null;
  const p = layout.panels[i];
  const r = (v) => Math.round(v * 100) / 100;
  if (layout.orientation === 'vertical') {
    const data = { x: r(p.innerStart), width: r(p.innerEnd - p.innerStart) };
    if (!widthOnly) { data.y = r(layout.cross.start); data.height = r(layout.cross.end - layout.cross.start); }
    return { data, panel: i, count: layout.panels.length, gutter: p.gutter };
  }
  const data = { y: r(p.innerStart), height: r(p.innerEnd - p.innerStart) };
  if (!widthOnly) { data.x = r(layout.cross.start); data.width = r(layout.cross.end - layout.cross.start); }
  return { data, panel: i, count: layout.panels.length, gutter: p.gutter };
};

/**
 * Derived "panel centre" guides — one per panel, at the middle of the strip between its fold lines (or fold line and page
 * edge) — so headings and pictures snap to the centre of a panel. Not stored, not printed; View ▸ Show ▸ Panel Centres.
 */
export const panelCenterGuidesOf = (guides = [], page) => {
  const layout = panelLayout(guides, page);
  if (!layout) return [];
  return layout.panels.map((p, i) => ({
    id: `panel-center-${i + 1}`,
    orientation: layout.orientation,
    position: Math.round(((p.start + p.end) / 2) * 100) / 100,
    kind: 'center',
    panel: i + 1,
    count: layout.panels.length,
  }));
};
